import React, { useContext, useCallback, useState, useRef } from "react";
import {
  Box,
  useTheme,
  useMediaQuery,
} from "@mui/material";
import { useNavigate } from "react-router-dom";
import { JSONSchema7 } from "json-schema";
import AutoAwesomeIcon from "@mui/icons-material/AutoAwesome";
import { EforceIncidentModel } from "../../shared/interfaces/EforceModels";

import {
  useFormDataStore,
  useFormDataTemporal,
  useCanUndo,
  useCanRedo,
} from "../../store/formDataStore";
import { BodycamContext } from "../../context/BodycamContext";
import { useErrorSnackbar } from "../../shared/hooks/useErrorSnackbar";
import { useInactivityTimer } from "../../shared/hooks/useInactivityTimer";
import { useLayout, usePageHeader } from "../../context/LayoutContext";

import { EditExtractedInformation } from "./EditExtractedInformation";
import { EditExtractedInformationSkeleton } from "../../components/skeletons/EditExtractedInformationSkeleton";
import { WarningDisplay } from "../../components/feedback/WarningDisplay";
import { SnackBarMessage } from "../../modals/SnackBarMessage";
import { useSnackbarPosition } from "../../shared/hooks/useSnackbarPosition";
import { InactivityModal } from "../../modals/InactivityModal";

import { useReportData } from "./hooks/useReportData";
import { useReportWebSocket } from "./hooks/useReportWebSocket";
import { useReportSaving } from "./hooks/useReportSaving";
import { EditSectionFooter } from "./components/EditSectionFooter";
import { ChatPanelContainer } from "../../components/chat/ChatPanelContainer";

import { eforceSchemaMapping } from "../../config/eforceSchemaMapping";
import { genericSchemaMapping } from "../../config/genericSchemaMapping";
import { AGENT_API_BASE_URL } from "../../shared/constants";
import { getReportInformationProperties } from "./formUtils";

const EditSectionPage: React.FC = () => {
  const navigate = useNavigate();
  const theme = useTheme();
  const isLgUp = useMediaQuery(theme.breakpoints.up("lg"));
  const { userInfo } = useContext(BodycamContext);
  const [userProfile] = userInfo;

  // Use global layout context for chat visibility
  const { setSidePanelOpen, setChatAvailable } = useLayout();

  // Ref to expose chat's sendMessage function to narrative widgets
  const chatSendMessageRef = useRef<
    ((message: string) => Promise<void>) | null
  >(null);

  // Ref to capture form data snapshot before agent changes (for change detection)
  const formDataSnapshotRef = useRef<EforceIncidentModel | null>(null);

  // Set page header in app bar
  usePageHeader({
    title: "Generated Report Editing",
    subtitle:
      "Use the Save button to save your changes. Sign off on every section to reach final review.",
    icon: <AutoAwesomeIcon />,
  });

  const {
    showError,
    snackBarMessage,
    snackBarMode,
    handleCloseSnackbar,
    showSuccess,
  } = useErrorSnackbar();
  const snackbarPosition = useSnackbarPosition();

  const formData = useFormDataStore((state) => state.formData);
  const setFormData = useFormDataStore((state) => state.setFormData);
  const setCurrentSection = useFormDataStore(
    (state) => state.setCurrentSection,
  );
  const { undo, redo } = useFormDataTemporal();
  const canUndo = useCanUndo();
  const canRedo = useCanRedo();

  // Custom data hooks
  const {
    reportID,
    videoData,
    setVideoData,
    videoLoadStatus,
    showRMSCommit,
    setShowRMSCommit,
    loading,
    jsonSchema,
    reportType,
    setReportType,
    lastModifiedTime,
    setLastModifiedTime,
    enableEditing,
    reFetchExtractedIncident,
    reportRms,
  } = useReportData();

  // Wrapped fetch that captures snapshot before fetching (for agent change detection)
  const fetchReportDataWithSnapshot = useCallback(async () => {
    // Capture current form data BEFORE the fetch
    const currentFormData = useFormDataStore.getState().formData;
    formDataSnapshotRef.current = currentFormData
      ? JSON.parse(JSON.stringify(currentFormData))
      : null;

    console.log("[Agent Refresh] Captured snapshot before fetch:", {
      hasSnapshot: !!formDataSnapshotRef.current,
    });

    // Call the actual fetch
    await reFetchExtractedIncident();
  }, [reFetchExtractedIncident]);

  // Set chat availability on mount and control visibility based on editing permission
  React.useEffect(() => {
    setChatAvailable(true);
    if (enableEditing) {
      setSidePanelOpen(true);
    } else {
      setSidePanelOpen(false);
    }
    return () => {
      setChatAvailable(false);
      setSidePanelOpen(false);
    };
  }, [setChatAvailable, setSidePanelOpen, enableEditing]);

  // Inactivity Timer State (managed here to coordinate with WS)
  const [inactivityExpiryTimestamp, setInactivityExpiryTimestamp] =
    useState<number>((Date.now() + 60000 * 5) / 1000);

  // Warnings State
  const [warnings, setWarnings] = useState<string[]>([]);

  // Inactivity Timer Hook
  const handleSessionTimeout = useCallback(() => {
    // Server drives timeout (close code 3008)
  }, []);

  const {
    showWarning: showTimeoutWarning,
    setWarningState,
    clearInactivityTimer,
  } = useInactivityTimer({
    countdownDuration: 1 * 60 * 1000,
    onTimeout: handleSessionTimeout,
  });

  // Saving Logic Hook
  const liveWebSocket = React.useRef<WebSocket | null>(null);

  const {
    isSaving,
    setIsSaving,
    saveDeferredRef,
    handleSaveAndExit,
    handleTimeoutSave,
    handleInstantSave,
    handleSaveCurrentChanges,
  } = useReportSaving({ liveWebSocket, showError });

  // WebSocket Hook
  const { handleUserAction, continueSession } = useReportWebSocket({
    reportID,
    enableEditing,
    showError,
    showSuccess,
    showTimeoutWarning,
    setWarningState,
    setInactivityExpiryTimestamp,
    refetchIncident: reFetchExtractedIncident,
    setIsSaving,
    saveDeferredRef,
    setLastModifiedTime,
    setShowRMSCommit,
    setReportType,
    setVideoData,
    setWarnings,
    liveWebSocket,
  });

  const schemaMappingMap = {
    EF: eforceSchemaMapping,
    KAR: genericSchemaMapping,
  };

  const getSchemaObjectProperties = (schema: JSONSchema7): string[] => {
    if (!schema.properties) return [];
    const properties: string[] = [];
    for (const key in schema.properties) {
      const curProp = schema.properties[key] as JSONSchema7;
      if (curProp.type?.includes("array") || curProp.type?.includes("object")) {
        properties.push(key);
      }
    }
    return properties;
  };

  const handleUndo = () => {
    undo();
    handleUserAction("Undo change");

    if (!jsonSchema) return;

    const { currentSection } = useFormDataStore.getState();
    const sections = getSchemaObjectProperties(jsonSchema);
    const totalSections = 1 + sections.length;

    if (currentSection >= totalSections) {
      setCurrentSection(totalSections - 1);
    }
  };

  const handleRedo = () => {
    redo();
    handleUserAction("Redo change");

    if (!jsonSchema) return;

    const { currentSection } = useFormDataStore.getState();
    const sections = getSchemaObjectProperties(jsonSchema);
    const totalSections = 1 + sections.length;

    if (currentSection >= totalSections) {
      setCurrentSection(totalSections - 1);
    }
  };

  const handleUpdate = async (updatedModel: EforceIncidentModel, save: boolean) => {
    const newModel = JSON.parse(JSON.stringify(updatedModel));
    setFormData(newModel);
    if (save) {
      await handleSaveCurrentChanges();
    }
  };

  const handleSubmit = async (narrativeOnly: boolean) => {
    if (!formData) {
      showError("No report data loaded to submit.");
      return;
    }

    let reportData: Partial<EforceIncidentModel>;
    let baseSchema: JSONSchema7 | null | undefined;
    if (narrativeOnly && jsonSchema) {
      const { data: generalInfoData, schema: generalInfoSchema } =
        getReportInformationProperties(jsonSchema, formData);
      reportData = {
        ...generalInfoData,
        Narratives: formData["Narratives"],
      };
      baseSchema = {
        ...jsonSchema,
        properties: {
          ...generalInfoSchema.properties,
          Narratives: jsonSchema?.properties?.Narratives,
        },
      } as JSONSchema7;
    } else {
      reportData = formData;
      baseSchema = jsonSchema;
    }

    // save before navigating — in narrative-only mode only General Information
    // and Narratives are persisted; other sections are left untouched.
    await handleSaveCurrentChanges(narrativeOnly ? reportData : undefined);
    navigate(`/final-edits/${reportID}`, {
      state: {
        modelData: reportData,
        showRMSCommit: showRMSCommit,
        sparseInfo: {},
        jsonSchema: baseSchema,
        schemaMapping:
          schemaMappingMap[reportRms as keyof typeof schemaMappingMap],
      },
    });
  };

  const handleContinueSession = useCallback(() => {
    clearInactivityTimer();
    continueSession();
  }, [clearInactivityTimer, continueSession]);

  return (
    <>
      <WarningDisplay warnings={warnings} onDismiss={() => setWarnings([])} />
      <InactivityModal
        open={showTimeoutWarning}
        onContinue={handleContinueSession}
        onLogout={handleTimeoutSave}
        expiryTimestamp={inactivityExpiryTimestamp}
      />

      <Box
        sx={{
          display: "flex",
          flexDirection: "column",
          flexGrow: 1,
          height: "calc(100vh - 96px)", // Full height minus app bar and padding
          overflow: "hidden", // Prevent outer scrolling
        }}
      >
        <SnackBarMessage
          handleClose={handleCloseSnackbar}
          message={snackBarMessage}
          mode={snackBarMode}
          horizontalAnchor="center"
          verticalAnchor="top"
          sxOverrides={snackbarPosition}
        />

        {/* Content Area + Sidebar Container */}
        <Box sx={{ display: "flex", flexGrow: 1, overflow: "hidden" }}>
          {/* Main Content Area */}
          <Box
            sx={{
              flexGrow: 1,
              display: "flex",
              flexDirection: "column",
              overflow: "hidden", // Allow children to scroll
              p: 3, // Inner padding for content
            }}
          >
            <Box
              sx={{
                flexGrow: 1,
                minHeight: 0,
                overflow: "auto",
                border: `1px solid ${theme.palette.divider}`,
                borderRadius: 1,
              }}
            >
              {loading || !formData ? (
                <EditExtractedInformationSkeleton />
              ) : (
                <EditExtractedInformation
                  formData={formData}
                  jsonSchema={jsonSchema as JSONSchema7}
                  schemaMapping={
                    schemaMappingMap[reportRms as keyof typeof schemaMappingMap]
                  }
                  onUpdate={handleUpdate}
                  onSubmit={handleSubmit}
                  videoData={videoData}
                  videoLoadStatus={videoLoadStatus}
                  handleUserActivity={handleUserAction}
                  liveWebSocket={liveWebSocket}
                  allowEditing={enableEditing}
                  isSaving={isSaving}
                  handleSaveChanges={handleSaveCurrentChanges}
                  showError={showError}
                  reportId={reportID}
                  reportType={reportType}
                  agentApiEndpoint={AGENT_API_BASE_URL}
                  fetchReportData={fetchReportDataWithSnapshot}
                  formDataSnapshotRef={formDataSnapshotRef}
                  userProfile={userProfile}
                  chatSendMessageRef={chatSendMessageRef}
                  setShowChat={setSidePanelOpen}
                  showRMSCommit={showRMSCommit}
                />
              )}
            </Box>

            <Box sx={{ mt: 2 }}>
              <EditSectionFooter
                enableEditing={enableEditing}
                lastModifiedTime={lastModifiedTime}
                isSaving={isSaving}
                canUndo={canUndo}
                canRedo={canRedo}
                onUndo={handleUndo}
                onRedo={handleRedo}
                onInstantSave={handleInstantSave}
                onSaveAndExit={handleSaveAndExit}
              />
            </Box>
          </Box>

          {/* Right Chat Sidebar - only show when user can edit (not read-only mode) */}
          {isLgUp && reportID && enableEditing && (
            <ChatPanelContainer
              mode="chat"
              chatProps={{
                liveWebSocket,
                agentApiEndpoint: AGENT_API_BASE_URL,
                reportId: reportID,
                reportType,
                handleSaveChanges: handleSaveCurrentChanges,
                fetchReportData: fetchReportDataWithSnapshot,
                showError,
                onSendMessageRefReady: (fn) => {
                  chatSendMessageRef.current = fn;
                },
              }}
            />
          )}
        </Box>
      </Box>
    </>
  );
};

export default EditSectionPage;
